/**
 * AuthContext - App-wide authentication state management
 * Handles login, logout, and API key validation
 */

import { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { getApiKey, setApiKey, clearApiKey, getStoredPartner, setStoredPartner } from '../services/auth'
import { validateApiKey, ApiError, prefetchCommonData } from '../services/api'

const AuthContext = createContext(null)

export function AuthProvider({ children }) {
  const [partner, setPartner] = useState(getStoredPartner)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)

  // Validate stored API key on mount
  useEffect(() => {
    async function checkAuth() {
      const apiKey = getApiKey()
      if (!apiKey) {
        setIsLoading(false)
        return
      }

      try {
        const partnerData = await validateApiKey()
        setPartner(partnerData)
        setStoredPartner(partnerData)
        setError(null)
        // Prefetch common data for faster navigation
        prefetchCommonData()
      } catch (err) {
        // Invalid API key - clear it
        if (err instanceof ApiError && err.status === 401) {
          clearApiKey()
          setPartner(null)
        } else {
          // Network error - keep stored partner but set error
          setError('Unable to connect to server')
        }
      } finally {
        setIsLoading(false)
      }
    }

    checkAuth()
  }, [])

  // Listen for unauthorized events from API service
  useEffect(() => {
    function handleUnauthorized() {
      setPartner(null)
      setError('Session expired - please log in again')
    }

    window.addEventListener('auth:unauthorized', handleUnauthorized)
    return () => window.removeEventListener('auth:unauthorized', handleUnauthorized)
  }, [])

  // Login with API key
  const login = useCallback(async (apiKey) => {
    setIsLoading(true)
    setError(null)

    try {
      // Store key first so validateApiKey can use it
      setApiKey(apiKey)
      const partnerData = await validateApiKey()
      setPartner(partnerData)
      setStoredPartner(partnerData)
      // Prefetch common data for faster navigation
      prefetchCommonData()
      return partnerData
    } catch (err) {
      // Clear invalid key
      clearApiKey()
      setPartner(null)

      if (err instanceof ApiError) {
        if (err.status === 401) {
          throw new Error('Invalid API key')
        }
        throw new Error(err.message)
      }
      throw new Error('Unable to connect to server')
    } finally {
      setIsLoading(false)
    }
  }, [])

  // Logout
  const logout = useCallback(() => {
    clearApiKey()
    setPartner(null)
    setError(null)
  }, [])

  // Refresh partner data
  const refreshPartner = useCallback(async () => {
    try {
      const partnerData = await validateApiKey()
      setPartner(partnerData)
      setStoredPartner(partnerData)
      return partnerData
    } catch (err) {
      // Don't clear on network errors
      if (err instanceof ApiError && err.status === 401) {
        logout()
      }
      throw err
    }
  }, [logout])

  const value = {
    partner,
    isAuthenticated: !!partner,
    isLoading,
    error,
    login,
    logout,
    refreshPartner,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

/**
 * Hook to access auth context
 * @returns {Object} Auth context value
 */
export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export default AuthContext
